/**
 * Texti: the text box state. A tap opens a textarea at the tap point (new text) or over an own
 * text (edit); commit adds/updates the text object (a new one is reported through onCreated, so it
 * can be selected at once), an emptied existing text is deleted.
 */
import { useCallback, useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { MAX_TEXT_CHARS, Q, unquant, type AnnotationKind, type AnnotationOf, type DataByKind } from '../../shared/annotations';
import type { SheetAnnotations } from '../annotations/contract';
import type { ViewerHandle } from '../viewer/types';
import { pageClientRect } from './dom';
import { pageToClient } from './geometry';

export interface TextDraft {
  page: number;
  /** Top-left of the text (quantised). */
  at: [number, number];
  /** Where the text box appears on screen. */
  client: { x: number; y: number };
  /** Existing text object being edited. */
  id: string | null;
  text: string;
  color: string;
  size: number;
  /** On-screen font size of the text at the current zoom (CSS px). */
  px: number;
  /** The sheet's engine when the box opened: a commit after switching sheets still lands there. */
  engine: SheetAnnotations;
}

/**
 * Creates an object in the active own layer and applies it (one undo step) — in `engine` when given,
 * else the current sheet's. Returns null (after telling the user) when the engine refused it.
 */
export type AddObject = <K extends AnnotationKind>(page: number, kind: K, data: DataByKind[K], engine?: SheetAnnotations) => AnnotationOf<K> | null;

export function useTextBox({
  engineRef,
  handleRef,
  addObject,
  onDeleted,
  onCreated,
}: {
  engineRef: RefObject<SheetAnnotations>;
  handleRef: RefObject<ViewerHandle | null>;
  addObject: AddObject;
  /** An existing text was emptied and deleted. */
  onDeleted: () => void;
  /** A new text was added, in `engine` (the sheet the box was opened on). */
  onCreated: (obj: AnnotationOf<'text'>, engine: SheetAnnotations) => void;
}) {
  const [text, setTextState] = useState<TextDraft | null>(null);
  const textRef = useRef<TextDraft | null>(null);
  const setText = useCallback((d: TextDraft | null) => {
    textRef.current = d;
    setTextState(d);
  }, []);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const draftFor = useCallback(
    (page: number, at: [number, number], size: number, color: string, id: string | null, value: string, client?: { x: number; y: number }): TextDraft => {
      const rect = pageClientRect(handleRef.current, page);
      const px = rect ? (size / Q) * rect.width : 17;
      let c = client;
      if (!c) {
        const p = rect ? pageToClient(rect, unquant(at[0]), unquant(at[1])) : null;
        c = p ? { x: p.clientX, y: p.clientY } : { x: 40, y: 120 };
      }
      return { page, at, client: c, id, text: value, color, size, px, engine: engineRef.current };
    },
    [handleRef, engineRef],
  );

  const open = useCallback(
    (d: TextDraft) => {
      // Mount synchronously so focus() still counts as part of the user's tap (iOS keyboard).
      flushSync(() => setText(d));
      textAreaRef.current?.focus();
    },
    [setText],
  );

  const commit = useCallback(() => {
    const d = textRef.current;
    if (!d) return;
    setText(null);
    const value = d.text.replace(/\s+$/u, '').slice(0, MAX_TEXT_CHARS);
    const eng = d.engine;
    if (d.id) {
      const o = eng.get(d.id);
      if (!o || o.kind !== 'text' || o.deletedAt !== null) return;
      if (!value.trim()) {
        eng.apply([{ type: 'delete', id: o.id }]);
        onDeleted();
      } else if (value !== o.data.text) eng.apply([{ type: 'update', id: o.id, data: { ...o.data, text: value } }]);
    } else if (value.trim()) {
      const obj = addObject(d.page, 'text', { at: d.at, text: value, color: d.color, size: d.size }, eng);
      if (obj) onCreated(obj, eng);
    }
  }, [addObject, onDeleted, onCreated, setText]);

  const cancel = useCallback(() => setText(null), [setText]);

  const setValue = useCallback(
    (value: string) => {
      const d = textRef.current;
      if (d) setText({ ...d, text: value });
    },
    [setText],
  );

  const openNew = useCallback(
    (page: number, at: [number, number], size: number, color: string, client: { x: number; y: number }) => open(draftFor(page, at, size, color, null, '', client)),
    [open, draftFor],
  );

  const edit = useCallback(
    (o: AnnotationOf<'text'>) => open(draftFor(o.page, [o.data.at[0], o.data.at[1]], o.data.size, o.data.color, o.id, o.data.text)),
    [open, draftFor],
  );

  return { text, textAreaRef, openNew, edit, commit, cancel, setValue, reset: cancel };
}
